import { unifiedApi } from "@/common";
import { ITileData } from "@/components/Home";
import {
  ISkyWebrootDeviceResponse,
  ISubscriberDownloadsInfo,
  ISubscription,
  WebrootDevicesInfoResponse,
} from "@/common/api/unifiedPortal/interfaces";
import { logEvent, logException } from "@/common/logger";
import { LAST_API_FETCH_INTERVAL } from "@/define";
import { defineStore } from "pinia";
import { computed, ref } from "vue";
import { fixMobileDevices, isExpired } from "@/components/Security/SecurityV2/SecurityPageHelper";

export const useSecurityPageStore = defineStore("securityPage", () => {
  const lastRequestTime = ref<number>(0);
  const webrootDeviceResult = ref<ISkyWebrootDeviceResponse | null>(null);
  const subscriptionInformationForDisplay = ref<ISubscription[]>([]);
  const subscriberInfo = ref<ISubscriberDownloadsInfo>();
  const subData = ref<ITileData[]>([]);
  const showSpinner = ref<boolean>(true);

  const webrootDevices = computed<WebrootDevicesInfoResponse[]>(() => {
    return webrootDeviceResult.value?.webrootDeviceResult || [];
  });

  const totalDevices = computed<number>(() => {
    return webrootDeviceResult.value?.totalDevices || 0;
  });

  const totalShields = computed<number>(() => {
    return webrootDeviceResult.value?.totalShields || 0;
  });

  const totalEnabledShields = computed<number>(() => {
    return webrootDeviceResult.value?.totalEnabledShields || 0;
  });

  const hasExpiredLicenses = computed<boolean>(() => {
    return webrootDeviceResult.value?.hasExpiredLicenses || false;
  });

  const hasLicenses = computed(() => {
    return subData.value.length > 0;
  });

  const activeLicenses = computed(() => {
    return subData.value.filter(sub => !isExpired(sub));
  });

  const expiredSubscriptions = computed(() => {
    return subData.value.filter(sub => isExpired(sub));
  });

  async function populateSecurityPageData(forceRefresh = false) {
    try {
      const currentTime = new Date().getTime();
      if (forceRefresh || currentTime - lastRequestTime.value >= LAST_API_FETCH_INTERVAL) {
        // API call
        webrootDeviceResult.value = (await unifiedApi.getSkyWebrootDevices()).data;
        //Fix mobile devices for display purposes
        webrootDeviceResult.value.webrootDeviceResult = fixMobileDevices(webrootDeviceResult.value.webrootDeviceResult);
        lastRequestTime.value = new Date().getTime();
        logEvent("security state updated", "security store", webrootDeviceResult.value);
      }
    } catch (err) {
      logException(err as Error, `SecurityPageStore_populateSecurityPageData`);
      // throw err as it handled in calling method.
      throw err;
    }
  }

  return {
    populateSecurityPageData,
    webrootDevices,
    totalDevices,
    totalShields,
    totalEnabledShields,
    hasExpiredLicenses,
    subscriptionInformationForDisplay,
    subscriberInfo,
    showSpinner,
    subData,
    hasLicenses,
    activeLicenses,
    expiredSubscriptions,
  };
});
